import { User, type Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { hasImpersonationRecord } from "@/lib/impersonation";
import { hashProfileEmailToken } from "@shared/profileEmailToken";

import type { Gender } from "@/lib/gender";

export type { Gender };

export interface ProfileEmail {
  id: number;
  email: string;
  is_primary: boolean | null;
}

export interface ProfileData {
  person: {
    id: number;
    first_name: string;
    last_name: string | null;
    gender: Gender | null;
    home_city: string | null;
    home_country: string | null;
    phone: string | null;
  };
  emails: ProfileEmail[];
}

const normalizeEmail = (raw: string): string => raw.trim().toLowerCase();

/** Split "Jane Q Doe" → ["Jane", "Q Doe"]. Empty input → ["", ""]. */
function splitFullName(fullName: string): [string, string] {
  const [first = "", ...rest] = fullName.trim().split(/\s+/);
  return [first, rest.join(" ")];
}

/** Look up the person_id linked to an email. */
async function findPersonIdByEmail(email: string): Promise<number | null> {
  const { data, error } = await supabase
    .from("person_email")
    .select("person_id")
    .eq("email", email)
    .maybeSingle();
  if (error) throw error;
  return data?.person_id ?? null;
}

/**
 * Fetch the full profile (person + all emails) linked to the signed-in user
 * by their email. Returns null if no match exists.
 */
export async function fetchMyProfile(userEmail: string): Promise<ProfileData | null> {
  const email = normalizeEmail(userEmail);
  if (!email) return null;

  const personId = await findPersonIdByEmail(email);
  if (personId === null) return null;

  const [personRes, emailsRes] = await Promise.all([
    supabase
      .from("person")
      .select("id, first_name, last_name, gender, home_city, home_country, phone")
      .eq("id", personId)
      .single(),
    supabase
      .from("person_email")
      .select("id, email, is_primary")
      .eq("person_id", personId)
      .order("is_primary", { ascending: false, nullsFirst: false })
      .order("id", { ascending: true }),
  ]);

  if (personRes.error) throw personRes.error;
  if (emailsRes.error) throw emailsRes.error;

  return {
    person: personRes.data,
    emails: emailsRes.data ?? [],
  };
}

/** Atomically save profile fields, primary selection and secondary removals. */
export async function updateMyEmails(
  primaryEmailId: number,
  removedEmailIds: number[] = [],
  person?: ProfileData["person"],
): Promise<ProfileEmail[]> {
  const { data, error } = await supabase.rpc("update_my_emails", {
    p_primary_email_id: primaryEmailId,
    p_removed_email_ids: removedEmailIds,
    ...(person ? { p_profile: person } : {}),
  });
  if (error) throw error;
  if (!data?.length)
    throw new Error("No email addresses returned. Please reload your profile.");
  return data;
}

export async function confirmProfileEmail(
  token: string,
): Promise<ProfileEmail[]> {
  if (!/^[0-9a-f]{64}$/.test(token))
    throw new Error("Invalid verification link. Request a new link.");
  const { data, error } = await supabase.rpc("confirm_profile_email", {
    p_token_hash: await hashProfileEmailToken(token),
  });
  if (error) throw error;
  if (!data?.length)
    throw new Error("No email addresses returned. Please reload your profile.");
  return data;
}

export async function signInWithProfileEmail(
  token: string,
): Promise<void> {
  if (hasImpersonationRecord())
    throw new Error("Leave read-only view-as mode before opening this link.");
  if (!/^[0-9a-f]{64}$/.test(token)) {
    throw new Error("Invalid email link. Please request a new link.");
  }
  const { data, error } = await supabase.functions.invoke<{ session: Session }>("send-email", {
    body: { formType: "profile_email_login", data: { token } },
  });
  if (error) throw error;
  if (!data?.session) throw new Error("The email link did not create a session.");
  const { error: sessionError } = await supabase.auth.setSession(data.session);
  if (sessionError) throw sessionError;
}

/**
 * Look up the person row linked to an email. Returns the person_id together
 * with the first/last name (used for header display and donor tracking).
 */
export async function fetchPersonNameByEmail(
  userEmail: string,
): Promise<{ person_id: number; first_name: string; last_name: string | null } | null> {
  const email = normalizeEmail(userEmail);
  if (!email) return null;

  const { data, error } = await supabase
    .from("person_email")
    .select("person_id, person:person_id(first_name, last_name)")
    .eq("email", email)
    .maybeSingle();
  if (error) throw error;
  const person = data?.person as { first_name: string; last_name: string | null } | null;
  if (!person || !data?.person_id) return null;
  return { person_id: data.person_id, ...person };
}

export type PersonProfileFields = ProfileData["person"];

/** Fetch editable profile fields by person id (used to prefill/gate the
 *  inline registration profile form). Throws if the row is missing. */
export async function fetchPersonProfileById(
  personId: number,
): Promise<PersonProfileFields> {
  const { data, error } = await supabase
    .from("person")
    .select("id, first_name, last_name, gender, home_city, home_country, phone")
    .eq("id", personId)
    .single();
  if (error) throw error;
  return data;
}

/** Update editable profile fields on the signed-in user's person row. */
export async function updateMyProfile(
  personId: number,
  fields: {
    firstName: string;
    lastName: string;
    gender: Gender | null;
    homeCity: string | null;
    homeCountry: string | null;
    phone: string | null;
  },
): Promise<void> {
  const { error } = await supabase
    .from("person")
    .update({
      first_name: fields.firstName.trim(),
      last_name: fields.lastName.trim() || null,
      gender: fields.gender,
      home_city: fields.homeCity,
      home_country: fields.homeCountry,
      phone: fields.phone,
    })
    .eq("id", personId);
  if (error) throw error;
}

/**
 * Auto-provision a person + person_email row for a newly signed-in user.
 * Idempotent: safe to call on every sign-in.
 */
export async function ensureProfileForUser(user: User): Promise<void> {
  if (!user.email) return;
  const fullName =
    (user.user_metadata?.full_name as string | undefined) ??
    (user.user_metadata?.name as string | undefined) ??
    "";
  const [firstName, lastName] = splitFullName(fullName);

  const { error } = await supabase.rpc("ensure_profile_for_email", {
    p_email: normalizeEmail(user.email),
    p_first_name: firstName,
    p_last_name: lastName,
  });
  if (error) throw error;
}
